import { ref } from "vue";
import type { User } from "../types/user";
import type { EPermission } from "../types/permission";
import { authTL, AuthTL, aiTL } from "../generated/tl";

// Module-level singleton (not a per-component ref) so App.vue's login gate and any other component
// (e.g. the Chair-picker's own-chair anchor) share the same current-user state without refetching.
const currentUser = ref<User | null>(null);
// "Acting as" chair for this login session (docs/tasks/022-journal.md část 1) — null until the
// user picks one (ChairGate.vue) or ChairSwitcherDialog.vue changes it.
const currentChairId = ref<number | null>(null);
// Admin-area permissions granted to the current user via their AppRoles (docs/tasks/027.md Část 1) —
// gates admin nav links/views. [] both while logged out and while logged in with no AppRole.
const currentPermissions = ref<EPermission[]>([]);
// Config-level kill switch (config/ai.neon's `aiProvider.enabled`) — independent of
// EPermission.useAiAssistant, so AI buttons must check BOTH before rendering.
const aiEnabled = ref(false);
const resolved = ref(false);

// All three only ever need the current session/cookie, not each other's result, so they're batched
// into one HTTP request (vendor/murdej/ts-link-php/docs/batch-mode.md) — on a throwaway instance,
// never the shared `authTL` singleton (useBatch() flips batchMode on PERMANENTLY with no way back,
// see docs/notes/index.md). aiTL.isEnabled() doesn't need auth and can't join that batch (different
// TL class), so it just runs alongside it.
const resolve = async () => {
  const [[user, chairId, permissions], enabled] = await Promise.all([
    Promise.all(new AuthTL().useBatch(tl => [tl.getCurrentUser(), tl.getCurrentChairId(), tl.getMyPermissions()])),
    aiTL.isEnabled(),
  ]);
  currentUser.value = user;
  currentChairId.value = chairId;
  currentPermissions.value = permissions as EPermission[];
  aiEnabled.value = enabled;
  resolved.value = true;
};

const setCurrentChair = async (chairId: number) => {
  await authTL.setCurrentChair(chairId);
  currentChairId.value = chairId;
};

const logout = async () => {
  await authTL.logout();
  currentUser.value = null;
  currentChairId.value = null;
  currentPermissions.value = [];
};

const hasPermission = (permission: EPermission) => currentPermissions.value.includes(permission);

export const useAuth = () => ({ currentUser, currentChairId, currentPermissions, aiEnabled, hasPermission, resolved, resolve, setCurrentChair, logout });
